/**
 * The upload half of "start an agreement" — entry choice, drop target, and the
 * summary that sits above the pre-filled form.
 *
 * F13 requires that parsing is best-effort and that the team confirms or
 * corrects before anything is saved. So this flow deliberately never ends in a
 * write: the most it can do is put values into the SAME form the manual path
 * uses, marked as derived, with the reviewer's finger still on the trigger.
 *
 * ── Why an entry choice instead of an "or upload" affordance on the form ────
 * The two paths start differently. Uploading first and reading the document is
 * what fills the form, so an upload control buried inside a form the user has
 * already begun typing into raises "does this overwrite what I entered?" — and
 * the honest answer would be yes. Choosing up front means the question never
 * arises, and the choice is reversible right up until the first keystroke.
 */

import { useRef, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import {
  extractFromDocument,
  needsReview,
  proposedCount,
  savedBlobPathFromFailure,
  type ExtractedField,
  type ExtractionResult,
  type ExtractResponse,
} from '@/features/hotel-supplier/lib/extraction';

/** Mirrors the API's own ceiling so the rejection happens before the upload,
 *  not after the bytes have crossed the wire. */
const MAX_BYTES = 10 * 1024 * 1024;

export function AgreementEntryChoice({
  onManual,
  onUpload,
}: {
  onManual: () => void;
  onUpload: () => void;
}) {
  return (
    <div className="section" data-testid="supplier-agreement-entry-choice">
      <div className="section-header">
        <span className="section-label">How do you want to start?</span>
      </div>
      <div className="section-body">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <button
            className="btn btn-primary"
            data-testid="supplier-agreement-entry-upload"
            onClick={onUpload}
            type="button"
          >
            Upload a completed agreement
          </button>
          <button
            className="btn btn-outline"
            data-testid="supplier-agreement-entry-manual"
            onClick={onManual}
            type="button"
          >
            Fill it in myself
          </button>
        </div>
        <p className="page-intro" style={{ marginTop: 12, marginBottom: 0 }}>
          Uploading reads what it can from the document and fills the form in
          for you to check. Nothing is saved until you review it.
        </p>
      </div>
    </div>
  );
}

export function AgreementDropzone({
  onExtracted,
  onSavedUnread,
  onBack,
}: {
  onExtracted: (res: ExtractResponse, filename: string) => void;
  /**
   * The document was STORED but could not be read — `extraction_failed`.
   *
   * A separate callback rather than a shape squeezed into `onExtracted`, because
   * there is no `ExtractionResult` on this path: nothing was read. What there IS is a
   * saved blob, and the banner promises the reviewer it is attached — so it has to
   * reach the create submit or that promise is a lie (which it was, twice).
   */
  onSavedUnread: (blobPath: string, filename: string) => void;
  onBack: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handle(file: File | undefined) {
    if (!file || busy) return;
    setError(null);

    if (file.size === 0) {
      setError('That file is empty.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(
        `That file is ${Math.round(file.size / (1024 * 1024))} MB — the limit is ${MAX_BYTES / (1024 * 1024)} MB.`,
      );
      return;
    }

    setBusy(true);
    try {
      const res = await extractFromDocument(file, apiFetch);
      onExtracted(res, file.name);
    } catch (err) {
      // A SAVED-BUT-UNREADABLE document is not a dead end, and treating it as one was
      // the defect. `extraction_failed` returns the blob path; carrying it forward is
      // what makes "the document is already attached" true, and it is the difference
      // between the reviewer typing the form with the file attached versus creating an
      // agreement with nothing attached and orphaning the upload.
      const saved =
        err instanceof ApiError ? savedBlobPathFromFailure(err.body) : null;
      if (saved) {
        onSavedUnread(saved, file.name);
        // No setBusy(false) / setError: we are leaving the dropzone for the manual
        // form, and the page says what happened in that context instead.
        return;
      }
      // The API's own message, verbatim — it is the authority on what was wrong.
      setError(
        err instanceof ApiError
          ? err.message
          : 'The document could not be uploaded. Try again, or fill the form in by hand.',
      );
      setBusy(false);
    }
  }

  return (
    <div className="section" data-testid="supplier-agreement-dropzone">
      <div className="section-header">
        <span className="section-label">Upload the agreement</span>
      </div>
      <div className="section-body">
        <div
          onDragLeave={() => setDragging(false)}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            void handle(e.dataTransfer.files?.[0]);
          }}
          style={{
            border: '2px dashed var(--text-faint)',
            borderRadius: 'var(--radius)',
            padding: '32px 16px',
            textAlign: 'center',
            // The only visual state change on drag-over. A colour swap alone
            // would be invisible to anyone who cannot distinguish it.
            opacity: dragging ? 0.7 : 1,
          }}
        >
          <div style={{ marginBottom: 12 }}>
            {busy
              ? 'Reading the document…'
              : 'Drop a PDF or CSV here, or choose a file.'}
          </div>
          <input
            accept=".pdf,.csv,.tsv,application/pdf,text/csv"
            data-testid="supplier-agreement-file-input"
            onChange={(e) => void handle(e.target.files?.[0])}
            ref={inputRef}
            style={{ display: 'none' }}
            type="file"
          />
          <button
            className="btn btn-outline"
            data-testid="supplier-agreement-choose-file"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            type="button"
          >
            Choose a file
          </button>
        </div>

        {error && (
          <div
            className="banner banner-error"
            data-testid="supplier-agreement-upload-error"
            style={{ marginTop: 12 }}
          >
            {error}
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <button
            className="btn btn-outline"
            data-testid="supplier-agreement-upload-back"
            onClick={onBack}
            type="button"
          >
            Fill it in myself instead
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * What was read, shown above the form the reviewer is about to check.
 *
 * Leads with the count and the filename because the first question after an
 * upload is "did that work?", and an empty form with no explanation reads as a
 * failure even when degrading to manual entry was the correct outcome.
 */
export function ExtractionSummary({
  extraction,
  filename,
  onDiscard,
}: {
  extraction: ExtractionResult;
  filename: string;
  onDiscard: () => void;
}) {
  const count = proposedCount(extraction);
  const uncertain = Object.values(extraction.fields).filter(needsReview).length;

  return (
    <div
      className="section"
      data-testid="supplier-agreement-extraction-summary"
    >
      <div className="section-header">
        <span className="section-label">From {filename}</span>
        <span className="section-meta">
          {count === 0
            ? 'nothing filled in'
            : `${count} field${count === 1 ? '' : 's'} filled in`}
        </span>
      </div>
      <div className="section-body">
        {extraction.note && (
          <p className="page-intro" style={{ marginTop: 0 }}>
            {extraction.note}
          </p>
        )}

        {count > 0 && (
          <p className="page-intro" style={{ marginTop: 0 }}>
            Check the values marked <DerivedGlyph /> before saving
            {uncertain > 0 ? (
              <>
                {' '}
                — <strong>{uncertain}</strong> of them{' '}
                {uncertain === 1 ? 'was' : 'were'} a guess and{' '}
                {uncertain === 1 ? 'needs' : 'need'} a closer look
              </>
            ) : null}
            . Nothing is saved until you submit.
          </p>
        )}

        {extraction.warnings.length > 0 && (
          <ul
            data-testid="supplier-agreement-extraction-warnings"
            style={{ margin: '0 0 12px', paddingLeft: 20 }}
          >
            {extraction.warnings.map((w) => (
              <li key={w} style={{ marginBottom: 4 }}>
                {w}
              </li>
            ))}
          </ul>
        )}

        {/* The unmapped list is how a real export tells us which aliases are
            missing. Shown to the user rather than logged, because the person
            holding the document is the one who can say what the column meant. */}
        {extraction.unmapped.length > 0 && (
          <p
            className="page-intro"
            data-testid="supplier-agreement-extraction-unmapped"
            style={{ marginTop: 0 }}
          >
            Not stored, because we have nowhere to put{' '}
            {extraction.unmapped.length === 1 ? 'it' : 'them'} yet:{' '}
            {extraction.unmapped.join(', ')}.
          </p>
        )}

        <button
          className="btn btn-outline"
          data-testid="supplier-agreement-discard-upload"
          onClick={onDiscard}
          type="button"
        >
          Start over with a blank form
        </button>
      </div>
    </div>
  );
}

/** The derived glyph, defined once so the legend and the field markers cannot
 *  drift apart and stop referring to the same thing. */
export function DerivedGlyph() {
  return <span aria-hidden="true">✎</span>;
}

/**
 * The marker beside a field that was read from the document.
 *
 * Carries the evidence in `title` AND in visually-hidden text, so the provenance
 * of a value is available to a screen reader rather than being hover-only. A
 * value the adapter guessed at is additionally called out in words — colour or
 * weight alone would not survive a greyscale print or a colour-blind reader.
 */
export function DerivedMark({ field }: { field: ExtractedField | undefined }) {
  if (!field) return null;
  const uncertain = needsReview(field);
  return (
    <span
      className="field-source"
      data-testid="supplier-agreement-derived-mark"
      data-uncertain={uncertain ? 'true' : 'false'}
      title={field.evidence}
    >
      {' '}
      <DerivedGlyph />
      <span className="sr-only">
        {uncertain
          ? 'Read from the document, uncertain: '
          : 'Read from the document: '}
        {field.evidence}
      </span>
      {uncertain && <span className="field-source-flag"> check</span>}
    </span>
  );
}

/** Shown once a human has replaced a derived value — feedback log §27 asks for
 *  the override to be recorded, not just for the mark to disappear. */
export function OverriddenMark() {
  return (
    <span
      className="field-source"
      data-testid="supplier-agreement-overridden-mark"
      title="You changed this after it was read from the document."
    >
      {' '}
      <span aria-hidden="true">•</span>
      <span className="sr-only">
        Changed by you after being read from the document
      </span>
    </span>
  );
}
